import React from 'react';
import styled from 'styled-components';
import { onlyNumeric } from '@origin-digital/normalize';
import { connect } from 'react-redux';
import { Button, Colors, Heading3, Metrics, Helpers } from '@origin-digital/style-guide';
import { Field, Form, Formik, FormikProps, FormikValues, FieldArrayRenderProps } from 'formik';
import analytics from '../../../../analytics';
import {
  HiddenField,
  PostcodeField,
  Select,
  TextField,
  DisqualificationAlert
} from '../../../../components';
import { CHECK_SERVICEABILITY } from '../../../../graphql/queries';
import { currencyTransform } from '../../../../utils/transforms';
import { isFieldTouched } from '../../../../utils/formHelpers';
import { createObjectFromQueryString } from '../../../../utils/queryHelpers';
import DisqualificationProvider from '../../../../components/DisqualificationProvider';
import { DisqualificationContext } from '../../../../components/DisqualificationProvider/disqualification-context';
import {
  CalculatorFormProps,
  CalculatorFormState,
  CalculatorFormValues
} from './CalculatorForm.types';
import { validation } from '../../validation';
import { updateCustomerDetail } from '../../../../redux/actions/customer';
import { checkIfPostcodeServiceable } from '../../../../redux/thunks';

import calculator from '../../content.json';
import { CalculatorValuesInput } from '../../../../../__generated__/globalTypes';

const Container = styled.div`
  background: ${Colors.WHITE};
  display: flex;
  justify-content: center;
  width: 100%;

  ${Helpers.media.LG`
     width: auto;
  `};
`;

const CardForm = styled(Form)`
  width: 100%;
  padding: ${Metrics.Spacing.M_Y_LG}px;
`;

export class CalculatorForm extends React.Component<CalculatorFormProps, CalculatorFormState> {
  constructor(props: CalculatorFormProps) {
    super(props);

    this.state = {
      triggerSubmit: false
    };
  }

  public componentDidMount() {
    const { initialValues } = this.props;
    const { averageBill, postcode, usagePercentDuringDay } = initialValues;

    if (averageBill && postcode && usagePercentDuringDay) {
      this.setState({
        triggerSubmit: true
      });
    }
  }

  public render() {
    const {
      loading,
      initialValues,
      validating,
      disqualifiedFields,
      updateCustomerDetail,
      checkIfPostcodeServiceable
    } = this.props;

    return (
      <DisqualificationProvider disqualificationMessages={calculator.content.disqualification}>
        <Formik
          initialValues={initialValues}
          validationSchema={validation}
          onSubmit={this.submit}
          render={({ values, isValid }: FormikProps<FormikValues>) => {
            const disableSubmit =
              values.hiddenPostcode === 'false' || values.hiddenPostcode === '' || !isValid;
            return (
              <Container>
                <CardForm>
                  <Heading3 text={calculator.content.form.title} marginBottom />
                  <Field
                    name={calculator.fields.postcode.name}
                    label={calculator.fields.postcode.label}
                    component={PostcodeField}
                    productType="SOLAR"
                    isDisqualified={disqualifiedFields.includes(
                      calculator.fields.hiddenPostcode.name
                    )}
                    transform={onlyNumeric}
                    maxLength={4}
                    changeHandler={updateCustomerDetail}
                    addressCheckHandler={checkIfPostcodeServiceable}
                    validating={validating}
                    parseGraphResult={this.parseGraphResult}
                    graphQuery={CHECK_SERVICEABILITY}
                  />
                  <DisqualificationContext.Consumer>
                    {context => (
                      <Field
                        name={calculator.fields.hiddenPostcode.name}
                        render={(helpers: FieldArrayRenderProps) => {
                          const touched: boolean = isFieldTouched(helpers.form, name);
                          return (
                            <React.Fragment>
                              <HiddenField
                                name={calculator.fields.hiddenPostcode.name}
                                touched={touched}
                                helpers={helpers}
                                disqualificationHandler={context.disqualificationHandler}
                              />
                              <DisqualificationAlert
                                name={calculator.fields.hiddenPostcode.name}
                                heading={calculator.content.disqualification.heading}
                                message={calculator.content.disqualification.postcode}
                              />
                            </React.Fragment>
                          );
                        }}
                      />
                    )}
                  </DisqualificationContext.Consumer>
                  <Field
                    name={calculator.fields.averageBill.name}
                    component={TextField}
                    label={calculator.fields.averageBill.label}
                    helpText={calculator.fields.averageBill.helpText}
                    transform={currencyTransform}
                    changeHandler={updateCustomerDetail}
                  />
                  <Field
                    name={calculator.fields.usagePercentDuringDay.name}
                    component={Select}
                    label={calculator.fields.usagePercentDuringDay.label}
                    options={calculator.fields.usagePercentDuringDay.options}
                    changeHandler={updateCustomerDetail}
                  />
                  <Button
                    type="submit"
                    data-id="calculateNowButton"
                    disabled={disableSubmit}
                    spinner={loading}
                    text="Calculate now"
                    block={true}
                    onClick={() =>
                      analytics.formSubmit('calculate-now', {
                        averageBill: initialValues.averageBill,
                        postcode: initialValues.postcode,
                        usagePercentDuringDay: initialValues.usagePercentDuringDay
                      })
                    }
                  />
                </CardForm>
              </Container>
            );
          }}
        />
      </DisqualificationProvider>
    );
  }

  private parseGraphResult = (data: any): void => {
    return data.serviceable;
  };

  private submit = (values: CalculatorFormValues) => {
    const { onSubmit } = this.props;
    let _values = { ...values };

    _values.averageBill = _values.averageBill.replace(/\$/g, '');
    if (onSubmit) {
      onSubmit(_values);
    }
  };
}

const mapStateToProps = (state: any) => {
  function initialValues(): CalculatorFormValues {
    const qs: CalculatorValuesInput = createObjectFromQueryString();

    return {
      averageBill: qs.bill ? `$${qs.bill}`.toString() : state.customer.averageBill,
      hiddenPostcode: '',
      package: 'advantage',
      postcode: qs.postcode ? qs.postcode : state.customer.postcode,
      usagePercentDuringDay: qs.mostUsage ? qs.mostUsage : state.customer.usagePercentDuringDay
    };
  }

  return {
    disqualifiedFields: state.disqualification.disqualifiedFields,
    initialValues: initialValues(),
    validating: state.request.validating
  };
};

export default connect(
  mapStateToProps,
  {
    checkIfPostcodeServiceable,
    updateCustomerDetail
  }
)(CalculatorForm);
